class TestimonialsSlider extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('.jp-testimonials__track-wrapper');
    this.track   = this.querySelector('.jp-testimonials__track');
    this.popup   = this.querySelector('.jp-testimonials__popup');

    // Move popup to <body> so position:fixed is never trapped by a parent stacking context
    if (this.popup) document.body.appendChild(this.popup);

    this.popupContent  = this.popup?.querySelector('.jp-testimonials__popup-content');
    this.popupLink     = this.popup?.querySelector('.jp-testimonials__popup-link');
    this.popupClose    = this.popup?.querySelector('.jp-testimonials__popup-close');
    this.popupBackdrop = this.popup?.querySelector('.jp-testimonials__popup-backdrop');

    this.popupOpen   = false;
    this.isDragging  = false;
    this.dragMoved   = false;
    this.justDragged = false;
    this.startX      = 0;
    this.startScrollLeft = 0;

    // The carousel track (arrows, progress bar, drag-to-scroll) only exists
    // in carousel mode. Cluster mode's auto-scrolling marquee rows have none
    // of this — but still need the popup/click wiring below.
    if (this.wrapper) {
      // ── Arrow navigation ──────────────────────────────────────────
      const prevBtn = this.querySelector('.jp-testimonials__arrow--prev');
      const nextBtn = this.querySelector('.jp-testimonials__arrow--next');
      prevBtn?.addEventListener('click', () => this.scrollByCards(-1));
      nextBtn?.addEventListener('click', () => this.scrollByCards(1));

      // ── Progress bar ──────────────────────────────────────────────
      // Drive the fill width directly from the wrapper's scroll position.
      // The scroll event fires for arrow smooth-scroll, drag, and touch.
      this._fill = this.querySelector('.jp-testimonials__progress-fill');
      this._onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.updateProgress();
        });
      };
      this.wrapper.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      window.addEventListener('load', () => this.updateProgress());
      // Initial paint (images may still be loading, so nudge a couple of times).
      this.updateProgress();
      requestAnimationFrame(() => this.updateProgress());
      setTimeout(() => this.updateProgress(), 400);

      // ── Mouse drag ────────────────────────────────────────────────
      this.wrapper.addEventListener('mousedown', this.onMouseDown.bind(this));
      window.addEventListener('mousemove', this.onMouseMove.bind(this));
      window.addEventListener('mouseup', this.onMouseUp.bind(this));
      this.wrapper.addEventListener('dragstart', e => e.preventDefault());
    }

    // ── Clickable cards ───────────────────────────────────────────
    this.querySelectorAll('.jp-testimonials__card--clickable').forEach(card => {
      card.addEventListener('click', () => {
        if (this.justDragged) return;
        this.openPopup(card);
      });
    });

    this.popupClose?.addEventListener('click',   () => this.closePopup());
    this.popupBackdrop?.addEventListener('click', () => this.closePopup());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.popupOpen) this.closePopup();
    });
  }

  disconnectedCallback() {
    if (this._onScroll) {
      this.wrapper?.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
  }

  // ── Carousel ──────────────────────────────────────────────────

  scrollByCards(dir) {
    const card = this.wrapper.querySelector('.jp-testimonials__card');
    if (!card) return;
    const gap  = parseFloat(getComputedStyle(this.track).gap) || 20;
    const step = card.offsetWidth + gap;
    this.wrapper.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  // ── Progress ──────────────────────────────────────────────────

  updateProgress() {
    if (!this.wrapper) return;
    if (!this._fill) this._fill = this.querySelector('.jp-testimonials__progress-fill');
    if (!this._fill) return;
    const max = this.wrapper.scrollWidth - this.wrapper.clientWidth;
    const pct = max > 1 ? (this.wrapper.scrollLeft / max) * 100 : 0;
    this._fill.style.width = Math.max(0, Math.min(100, pct)).toFixed(2) + '%';
  }

  // ── Popup ─────────────────────────────────────────────────────

  openPopup(card) {
    const type = card.dataset.popupType;
    if (!this.popup || !this.popupContent || !type) return;

    this.popupContent.innerHTML = '';

    if (type === 'image') {
      const src = card.dataset.popupSrc;
      if (!src) return;
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      this.popupContent.appendChild(img);

    } else if (type === 'video') {
      const sourcesRaw = card.dataset.videoSources;
      if (!sourcesRaw) return;
      let sources = [];
      try { sources = JSON.parse(sourcesRaw); } catch { return; }
      const video = document.createElement('video');
      video.controls    = true;
      video.autoplay    = true;
      video.playsinline = true;
      sources.forEach(s => {
        const el = document.createElement('source');
        el.src  = s.url;
        el.type = s.type;
        video.appendChild(el);
      });
      this.popupContent.appendChild(video);
    }

    const link = card.dataset.popupLink;
    if (this.popupLink) {
      if (link) {
        this.popupLink.href   = link;
        this.popupLink.hidden = false;
      } else {
        this.popupLink.hidden = true;
      }
    }

    this.popup.hidden = false;
    this.popupOpen    = true;
    document.body.style.overflow = 'hidden';
  }

  closePopup() {
    if (!this.popup) return;
    this.popup.hidden = true;
    this.popupOpen    = false;
    this.popupContent.innerHTML = '';
    if (this.popupLink) this.popupLink.hidden = true;
    document.body.style.overflow = '';
  }

  // ── Mouse drag (scroll) ───────────────────────────────────────

  onMouseDown(e) {
    if (e.button !== 0) return;
    this.isDragging      = true;
    this.dragMoved       = false;
    this.startX          = e.clientX;
    this.startScrollLeft = this.wrapper.scrollLeft;
    this.wrapper.style.cursor         = 'grabbing';
    this.wrapper.style.userSelect     = 'none';
    this.wrapper.style.scrollSnapType = 'none';
  }

  onMouseMove(e) {
    if (!this.isDragging) return;
    const delta = e.clientX - this.startX;
    if (Math.abs(delta) > 4) this.dragMoved = true;
    this.wrapper.scrollLeft = this.startScrollLeft - delta;
  }

  onMouseUp() {
    if (!this.isDragging) return;
    this.isDragging  = false;
    this.justDragged = this.dragMoved;
    this.wrapper.style.cursor         = '';
    this.wrapper.style.userSelect     = '';
    this.wrapper.style.scrollSnapType = '';
    setTimeout(() => { this.justDragged = false; }, 120);
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
